/**
 * ============================================================
 * SIMPLE SOLUTIONS — PRODUCT CONFIG
 * ============================================================
 * This is the ONLY file you need to edit when launching a
 * new product funnel. Every component reads from here.
 *
 * WORKFLOW FOR A NEW PRODUCT:
 * 1. Duplicate the entire Magic Patterns sales page design
 * 2. Open config.ts in the new design
 * 3. Fill in all values below
 * 4. Update copy in the section components as needed
 * 5. Publish and connect to your domain
 *
 * SHOPIFY SETUP CHECKLIST (per product):
 * [ ] Create product in Shopify, status: Active
 * [ ] Turn OFF inventory tracking (digital product)
 * [ ] Enable on Online Store sales channel
 * [ ] Upload PDF to Shopify Digital Downloads app
 * [ ] Copy the delivery.shopifyapps.com download URL
 * [ ] Create upsell product (if applicable)
 * [ ] Create VAULT10 discount code ($7 off upsell product)
 * [ ] Copy both variant IDs from the product URLs
 * [ ] Upload product mockup images to Shopify Files
 * [ ] Install Customer Events pixel for thank-you redirect
 * ============================================================
 */

export const CONFIG = {

  // ----------------------------------------------------------
  // STORE
  // ----------------------------------------------------------
  store: {
    // Your Shopify myshopify domain (no https://)
    domain: 'simplepersonalgrowth.store',
  },

  // ----------------------------------------------------------
  // MAIN PRODUCT
  // ----------------------------------------------------------
  product: {
    name: 'The AI Income Blueprint',
    tagline: 'How everyday people use ChatGPT and Claude to build real income.',
    price: 17,
    compareAtPrice: 34,
    priceBadge: '50% LAUNCH PRICE',

    // Shopify variant ID — get from: Shopify admin → Products → click variant → copy ID from URL
    variantId: '44874756980781',

    // From Shopify Digital Downloads app after uploading the PDF
    downloadUrl: 'https://delivery.shopifyapps.com/-/9153f7c05992011c/70e36437309121c1',

    // Shopify CDN URL after uploading to Files
    mockupUrl: 'https://cdn.shopify.com/s/files/1/0736/0194/1549/files/Screenshot2026-04-26230052.png?v=1777271554',
    bothProductsUrl: 'https://cdn.shopify.com/s/files/1/0736/0194/1549/files/bothproducts_0247a2e8-b7d2-48fe-b935-e5a722d861f8.png?v=1777271755',
  },

  // ----------------------------------------------------------
  // ORDER BUMP (upsell shown at checkout)
  // Set enabled: false to hide the bump entirely
  // ----------------------------------------------------------
  bump: {
    enabled: true,
    name: '50 Power Prompts',
    fullName: '50 Power Prompts: The Companion Vault',
    description: '50 deep, production-ready prompts across 10 categories. The companion vault to the Blueprint. Copy, paste, and get results the same day. Only offered at this price right here.',
    retailPrice: 17,
    bumpPrice: 10,
    savings: 7,

    // Shopify variant ID for the upsell product
    variantId: '44879668052013',

    // From Shopify Digital Downloads app
    downloadUrl: 'https://delivery.shopifyapps.com/-/c6db1d0716f52cde/94635c039fc2afd7',

    // Discount code created in Shopify Discounts
    // Amount: $7 off, applies to upsell product, min purchase $17
    discountCode: 'VAULT10',
  },

  // ----------------------------------------------------------
  // THANK-YOU PAGE
  // ----------------------------------------------------------
  thankYouPage: {
    url: 'https://project-ai-income-blueprint-thank-you-page-529.magicpatterns.app',
  },

  // ----------------------------------------------------------
  // TRACKING
  // ----------------------------------------------------------
  tracking: {
    // Meta Pixel ID — get from Meta Events Manager
    metaPixelId: '884777681203338',

    // Lead value for Facebook bid optimization
    leadValue: 17,
    currency: 'USD',
  },

  // ----------------------------------------------------------
  // BRAND
  // ----------------------------------------------------------
  brand: {
    name: 'Simple Solutions',
    supportEmail: 'support@marketingsimplesolutions.com',
    storeUrl: 'https://simplepersonalgrowth.store',
    privacyUrl: 'https://simplepersonalgrowth.store/policies/privacy-policy',
    termsUrl: 'https://simplepersonalgrowth.store/policies/terms-of-service',
    year: '2026',
  },

  // ----------------------------------------------------------
  // SALES PAGE COPY — update per product
  // ----------------------------------------------------------
  copy: {
    heroHeadline: ['YOU\'VE GOT', 'CHATGPT OPEN.', 'NOW WHAT?'],
    heroHighlight: 'NOW WHAT?',
    heroSubtext: 'Most people use AI for 3 days, then close the tab. The ones making real money with it have one thing you don\'t: a system.',
    heroCta: 'GET THE BLUEPRINT · $17',

    offerHeadline: 'ONE TIME. $17. YOURS FOREVER.',
    offerIncludes: [
      '65-page PDF (lifetime access, lifetime updates)',
      '7 income paths with workflows, prompts, first-client scripts',
      '30-day launch plan with daily checklists',
      '100 copy-paste prompts organized by path',
      '3 printable worksheets (Path Picker, $500 Tracker, 30-Day Tracker)',
      '"How to Start Monday" bonus checklist',
    ],
  },
}

/**
 * ============================================================
 * CART URL BUILDER
 * ============================================================
 * Generates the correct Shopify cart permalink.
 * Used by Button.tsx — don't call this directly.
 *
 * Single product:  /cart/VARIANT:1
 * With bump:       /cart/VARIANT:1,BUMP:1?discount=CODE&bundle=true
 *
 * The ?bundle=true param tells the thank-you page pixel
 * to show both download buttons.
 * ============================================================
 */
export function buildCartUrl(withBump: boolean): string {
  const { domain } = CONFIG.store
  const { variantId } = CONFIG.product
  const { variantId: bumpId, discountCode } = CONFIG.bump

  if (withBump && CONFIG.bump.enabled) {
    // Shopify applies the discount automatically at checkout
    // when passed as a URL param — buyer never has to type it
    return `https://${domain}/cart/${variantId}:1,${bumpId}:1?discount=${discountCode}&bundle=true`
  }
  return `https://${domain}/cart/${variantId}:1`
}

/**
 * ============================================================
 * BUTTON LABEL BUILDER
 * ============================================================
 */
export function buildButtonLabel(withBump: boolean): string {
  if (withBump && CONFIG.bump.enabled) {
    const total = CONFIG.product.price + CONFIG.bump.bumpPrice
    return `GET BLUEPRINT + 50 PROMPTS · $${total}`
  }
  return `GET THE BLUEPRINT · $${CONFIG.product.price}`
}
